import { Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

import { getDesignTokens } from '../core';
import TextField from '../text-field';

/**
 * ⚠ THIS BAR DOES NOT SCROLL ITS `children`, AND ANYTHING WIDER THAN THE
 * CONTAINER IS SILENTLY LOST — no scrollbar, no ellipsis, no overflow.
 *
 * Measured in a browser on 2026-08-26 at a 375px viewport:
 *
 *   Review's filter group   293px inside a 293px parent  → fits
 *   Runs' filter group      348px inside a 293px parent  → CLIPPED,
 *                                                          "Completed (131)"
 *                                                          unreachable
 *
 * ⇒ REVIEW FITS BY LUCK, NOT BY DESIGN. Its four labels happen to total exactly
 * the container width. Runs has five labels and one long one ("Awaiting spine
 * review"), and went over. Nothing in this component prevents that, and nothing
 * reports it — every gate (eslint, tsc, jest, the dev-server compile) was green
 * while a filter was unreachable.
 *
 * A control that works because of the length of today's strings is a defect
 * with a timer on it: the next screen with longer labels, or a translation,
 * inherits a failure that has never fired. This bar has 13 call sites.
 *
 * **If you pass a ToggleButtonGroup or any fixed-width control as `children`,
 * wrap it yourself:** `<Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>`.
 * See `controlgen/runs.tsx` for the pattern.
 *
 * Left as a warning rather than a fix on purpose — changing the layout of a
 * component with 13 call sites is not a change to make in passing, and it is
 * not this lane's to make unasked.
 */
interface Props {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSearchChange?: () => void;
  children?: React.ReactNode;
  className?: string;
}

const SearchAndFiltersBarImpl: React.FC<Props> = ({
  placeholder = 'Search...',
  value,
  onChange,
  onSearchChange,
  children,
  className,
}) => {
  const theme = useTheme();
  const t = getDesignTokens(
    theme.palette.mode as 'light' | 'dark',
    theme.accentPalette
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    onSearchChange?.();
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        mb: 2,
        '& .search-field': { flex: 1, minWidth: 200 },
        '& .filter-field': { minWidth: 150 },
        '& .MuiOutlinedInput-root': {
          backgroundColor: `${t.surface1} !important`,
          '&:hover': { backgroundColor: `${t.surface1} !important` },
          '& .MuiInputBase-input, & .MuiSelect-select': {
            color: t.textPrimary,
            '&::placeholder': { color: t.textPlaceholder, opacity: 1 },
          },
        },
      }}
    >
      <TextField
        className="search-field"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        variant="outlined"
        size="small"
      />
      {children}
    </Box>
  );
};

const SearchAndFiltersBar = styled(SearchAndFiltersBarImpl)({});

export default SearchAndFiltersBar;
